/* Admin Help Center & NHF Administrator Certification.
 * Dedicated /admin/help route — sidebar by category, topic detail with the
 * 3-question structure (What / When / What if not), NHF-specific examples,
 * steps, mistakes, best practices, related route deep-link.
 *
 * Tracks per-user completion of certification topics with a progress bar.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  BookOpen, Search, CheckCircle, Circle, ChevronRight, ChevronDown, Loader2,
  ArrowLeft, ExternalLink, GraduationCap, Lightbulb, AlertTriangle, ListChecks,
  HelpCircle, Sparkles, Star, Edit3, Download, Video, Clock, Target,
} from 'lucide-react';

import DocEditorDrawer from '../../components/admin/DocEditorDrawer';
import QuizTaker from '../../components/admin/QuizTaker';
import NotificationsBell from '../../components/admin/NotificationsBell';
import LanguageSelector from '../../components/LanguageSelector';
import TranslatedPage from '../../components/TranslatedPage';
import MyCertificates from '../../components/admin/MyCertificates';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminHelp = () => {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [certPath, setCertPath] = useState([]);
  const [progress, setProgress] = useState({ completed_topics: [], certification: { percent: 0 } });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(search.get('topic') || 'welcome');
  const [q, setQ] = useState('');
  const [openCategory, setOpenCategory] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const { user } = useAuth();
  const isSuper = ['super_admin', 'admin'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/docs`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/docs-progress`, { withCredentials: true }),
      ]);
      setDocs(a.data?.docs || []);
      setCategories(a.data?.categories || []);
      setCertPath(a.data?.certification_path || []);
      setProgress(b.data || { completed_topics: [], certification: { percent: 0 } });
      setTracks(b.data?.tracks || []);
    } catch (e) { /* swallow */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = search.get('topic');
    if (t) setActiveId(t);
  }, [search]);

  const active = useMemo(() => docs.find((d) => d.id === activeId), [docs, activeId]);
  const completed = useMemo(() => new Set(progress.completed_topics || []), [progress]);

  // Filter docs by search query.
  const visible = useMemo(() => {
    if (!q.trim()) return docs;
    const lo = q.toLowerCase();
    return docs.filter((d) =>
      d.title.toLowerCase().includes(lo)
      || (d.what_it_does || '').toLowerCase().includes(lo)
      || (d.when_to_use || '').toLowerCase().includes(lo));
  }, [docs, q]);

  const byCategory = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = [];
    for (const d of visible) {
      if (!m[d.category]) m[d.category] = [];
      m[d.category].push(d);
    }
    return m;
  }, [visible, categories]);

  // Auto-open the category that holds the active topic.
  useEffect(() => {
    if (active && openCategory === null) setOpenCategory(active.category);
  }, [active, openCategory]);

  const markCompleted = async (docId, next = true) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/docs-progress/${docId}`,
        { completed: next }, { withCredentials: true });
      const r = await axios.get(`${BACKEND_URL}/api/admin/docs-progress`, { withCredentials: true });
      setProgress(r.data || progress);
    } catch (e) { /* swallow */ }
  };

  const cert = progress.certification || { percent: 0, completed: 0, total: 0 };

  return (
    <TranslatedPage context="admin_help">
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-help-page">
      {/* Top bar */}
      <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/admin/dashboard')}
              className="text-xs px-2 py-1 border border-[#E5E7EB] rounded inline-flex items-center gap-1 hover:bg-[#FAFBFC]"
              data-testid="help-back">
              <ArrowLeft className="w-3 h-3" /> Dashboard
            </button>
            <h1 className="font-heading text-lg font-bold text-[#0A0A0A] inline-flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#BA1826]" /> Help Center & Training
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="hidden md:flex items-center gap-2 text-xs">
              <GraduationCap className="w-3.5 h-3.5 text-[#BA1826]" />
              <span className="font-semibold text-[#0A0A0A]">NHF Admin Certification</span>
              <div className="w-32 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                <div className="h-full bg-[#BA1826] transition-all" style={{ width: `${cert.percent || 0}%` }} />
              </div>
              <span className="text-[#4B5563] font-mono" data-testid="cert-progress">
                {cert.completed || 0} / {cert.total || 0} ({cert.percent || 0}%)
              </span>
              {cert.certified && <Star className="w-3.5 h-3.5 text-amber-500" />}
            </div>
            <NotificationsBell />
            <LanguageSelector />
            <div className="relative">
              <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Help…"
                className="pl-7 pr-2 py-1.5 border border-[#E5E7EB] rounded text-xs w-64"
                data-testid="help-search" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 shrink-0 bg-white border-r border-[#E5E7EB] min-h-screen p-3 overflow-y-auto"
          data-testid="help-sidebar">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
          ) : (
            categories.map((cat) => {
              const items = byCategory[cat.id] || [];
              if (items.length === 0 && q) return null;
              const isOpen = openCategory === cat.id;
              return (
                <div key={cat.id} className="mb-1" data-testid={`help-cat-${cat.id}`}>
                  <button onClick={() => setOpenCategory(isOpen ? null : cat.id)}
                    className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-wider font-bold text-[#4B5563] hover:bg-[#FAFBFC] rounded">
                    <span>{cat.label}</span>
                    {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </button>
                  {isOpen && (
                    <div className="ml-2 mt-0.5 mb-1.5">
                      {items.map((d) => {
                        const done = completed.has(d.id);
                        const inCert = certPath.includes(d.id);
                        return (
                          <button key={d.id} onClick={() => setActiveId(d.id)}
                            className={`w-full text-left px-2 py-1.5 text-xs rounded inline-flex items-center gap-2 group ${
                              activeId === d.id ? 'bg-[#FFF8F8] text-[#BA1826] font-semibold' : 'text-[#0A0A0A] hover:bg-[#FAFBFC]'
                            }`}
                            data-testid={`help-topic-${d.id}`}>
                            {done
                              ? <CheckCircle className="w-3 h-3 text-green-600 shrink-0" />
                              : <Circle className="w-3 h-3 text-[#E5E7EB] shrink-0" />}
                            <span className="truncate flex-1">{d.title}</span>
                            {d.difficulty && (
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${(DIFFICULTY_STYLES[d.difficulty] || DIFFICULTY_STYLES.Intermediate).dot}`}
                                title={`${d.difficulty} lesson`} />
                            )}
                            {d.estimated_minutes && (
                              <span className="text-[9px] text-[#9CA3AF] tabular-nums shrink-0"
                                title={`Estimated ${d.estimated_minutes} min`}
                                data-testid={`help-topic-time-${d.id}`}>
                                {d.estimated_minutes}m
                              </span>
                            )}
                            {inCert && <GraduationCap className="w-3 h-3 text-[#BA1826]/60 shrink-0" title="Part of certification" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </aside>

        {/* Topic detail */}
        <main className="flex-1 p-6 overflow-y-auto" data-testid="help-topic-detail">
          {loading || !active ? (
            <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
          ) : (
            <div className="max-w-3xl">
              <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">{active.title}</h1>

              {/* Estimated time + Certification badge — one quick glance */}
              <div className="flex items-center gap-3 mb-3 flex-wrap" data-testid="topic-meta">
                <span className="text-[11px] inline-flex items-center gap-1 text-[#4B5563]"
                  data-testid="topic-estimated-time">
                  <Clock className="w-3.5 h-3.5 text-[#BA1826]" />
                  <span className="font-semibold">{active.estimated_minutes || 5} min</span>
                  <span className="text-[#9CA3AF]">read</span>
                </span>
                <span className="text-[11px] text-[#9CA3AF]">·</span>
                <DifficultyBadge level={active.difficulty} testid="topic-difficulty" />
                <span className="text-[11px] text-[#9CA3AF]">·</span>
                <span className="text-[11px] text-[#9CA3AF]">
                  {(categories.find((c) => c.id === active.category) || {}).label}
                </span>
              </div>

              {/* "What You Will Learn" objectives — Phase 1 */}
              {(active.objectives || []).length > 0 && (
                <div className="mb-5 bg-[#FFF8F8] border border-[#BA1826]/20 rounded-lg p-3"
                  data-testid="topic-objectives">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#BA1826] inline-flex items-center gap-1.5 mb-2">
                    <Target className="w-3.5 h-3.5" /> What you will learn
                  </p>
                  <ul className="space-y-1">
                    {active.objectives.map((o, i) => (
                      <li key={i} className="text-sm text-[#0A0A0A] flex items-start gap-2"
                        data-testid={`topic-objective-${i}`}>
                        <CheckCircle className="w-3.5 h-3.5 text-[#BA1826] shrink-0 mt-0.5" />
                        <span>{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Certification + mark complete */}
              <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="topic-controls">
                {certPath.includes(active.id) && (
                  <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 bg-[#FFF8F8] text-[#BA1826] border border-[#BA1826]/30 rounded inline-flex items-center gap-1">
                    <GraduationCap className="w-3 h-3" /> Certification Topic {certPath.indexOf(active.id) + 1}
                  </span>
                )}
                {active.related_route && (
                  <Link to={active.related_route}
                    className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded inline-flex items-center gap-1 hover:bg-[#FAFBFC]"
                    data-testid="topic-open-feature">
                    Open this feature <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
                {isSuper && (
                  <button onClick={() => setShowEditor(true)}
                    className="text-[11px] px-2 py-1 border border-[#BA1826]/40 bg-[#FFF8F8] text-[#BA1826] rounded inline-flex items-center gap-1 hover:bg-[#BA1826] hover:text-white transition-colors"
                    data-testid="topic-edit-btn">
                    <Edit3 className="w-3 h-3" /> Edit topic
                  </button>
                )}
                <button onClick={() => markCompleted(active.id, !completed.has(active.id))}
                  className={`text-[11px] px-3 py-1 rounded font-semibold inline-flex items-center gap-1 ${
                    completed.has(active.id)
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-[#BA1826] text-white hover:bg-[#9F1421]'
                  }`}
                  data-testid="topic-mark-complete">
                  {completed.has(active.id)
                    ? <><CheckCircle className="w-3 h-3" /> Completed</>
                    : <><Circle className="w-3 h-3" /> Mark complete</>}
                </button>
              </div>

              {/* The 3 mandatory questions */}
              <Section icon={HelpCircle} title="What does this do?" accent="brand"
                body={active.what_it_does} testid="topic-what" />
              <Section icon={Sparkles} title="When should I use it?" accent="green"
                body={active.when_to_use} testid="topic-when" />
              <Section icon={AlertTriangle} title="What happens if I don't use it correctly?" accent="amber"
                body={active.what_if_not} testid="topic-if-not" />

              {active.who_should_use && (
                <Section icon={GraduationCap} title="Who should use this" accent="indigo"
                  body={active.who_should_use} testid="topic-who" />
              )}

              {(active.steps || []).length > 0 && (
                <div className="mb-4" data-testid="topic-steps">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-2 inline-flex items-center gap-1.5">
                    <ListChecks className="w-3.5 h-3.5 text-[#BA1826]" /> Step-by-step
                  </p>
                  <ol className="list-decimal list-inside text-sm text-[#0A0A0A] space-y-1 pl-2">
                    {active.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                </div>
              )}

              {active.nhf_example && (
                <div className="mb-4 bg-[#FFF8F8] border border-[#BA1826]/30 rounded-lg p-3"
                  data-testid="topic-example">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#BA1826] mb-1 inline-flex items-center gap-1.5">
                    <Lightbulb className="w-3.5 h-3.5" /> Real-world NHF example
                  </p>
                  <p className="text-sm text-[#0A0A0A] leading-relaxed">{active.nhf_example}</p>
                </div>
              )}

              {(active.common_mistakes || []).length > 0 && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3"
                  data-testid="topic-mistakes">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-1.5 inline-flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Common mistakes to avoid
                  </p>
                  <ul className="list-disc list-inside text-sm text-amber-900 space-y-0.5 pl-1">
                    {active.common_mistakes.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}

              {(active.best_practices || []).length > 0 && (
                <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3"
                  data-testid="topic-best">
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-800 mb-1.5 inline-flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /> Best practices
                  </p>
                  <ul className="list-disc list-inside text-sm text-emerald-900 space-y-0.5 pl-1">
                    {active.best_practices.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}

              {(active.media_embeds || []).length > 0 && (
                <div className="mb-4 space-y-2" data-testid="topic-embeds">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563] inline-flex items-center gap-1.5">
                    <Video className="w-3.5 h-3.5 text-[#BA1826]" /> Watch the walkthrough
                  </p>
                  {active.media_embeds.map((m, i) => <MediaEmbed key={i} embed={m} idx={i} />)}
                </div>
              )}

              {(active.downloads || []).length > 0 && (
                <div className="mb-4 bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="topic-downloads">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-2 inline-flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5 text-[#BA1826]" /> Downloads
                  </p>
                  <ul className="space-y-1">
                    {active.downloads.map((d, i) => (
                      <li key={i}>
                        <a href={d.url} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-[#BA1826] hover:underline inline-flex items-center gap-1.5"
                          data-testid={`topic-download-${i}`}>
                          <Download className="w-3.5 h-3.5" />
                          {d.label} <span className="text-[10px] text-[#9CA3AF] uppercase">{d.type}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(active.glossary || []).length > 0 && (
                <div className="mb-4 bg-[#FAFBFC] border border-[#E5E7EB] rounded-lg p-3"
                  data-testid="topic-glossary">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1.5">Glossary</p>
                  <dl className="text-sm space-y-1">
                    {active.glossary.map((g, i) => (
                      <div key={i}>
                        <dt className="font-semibold text-[#0A0A0A] inline">{g.term}: </dt>
                        <dd className="inline text-[#4B5563]">{g.definition}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* Certification path navigator */}
              {certPath.includes(active.id) && (
                <CertNav active={active.id} path={certPath} docs={docs} setActiveId={setActiveId} />
              )}

              <QuizTaker docId={active.id} onPassed={load} />

              <p className="mt-6 text-[10px] text-[#9CA3AF]">
                Topic id: <code>{active.id}</code> · Last updated:{' '}
                {active.updated_at ? new Date(active.updated_at).toLocaleString() : '—'}
              </p>
            </div>
          )}
        </main>
      </div>

      {/* Multi-track progress strip — appears below for quick role-based onboarding */}
      {!loading && tracks.length > 0 && (
        <div className="px-6 py-5 bg-white border-t border-[#E5E7EB]" data-testid="cert-tracks-panel">
          <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-2 inline-flex items-center gap-1.5">
            <GraduationCap className="w-3.5 h-3.5 text-[#BA1826]" /> Certification tracks
          </p>
          <p className="text-[11px] text-[#9CA3AF] mb-3 max-w-3xl">
            Pick the track that matches your role. Topics inside each track auto-mark as you read & complete them
            on this Help Center.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {tracks.map((t) => (
              <div key={t.id} className="border border-[#E5E7EB] rounded-lg p-3 bg-[#FAFBFC]"
                data-testid={`cert-track-${t.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-xs font-bold text-[#0A0A0A]">{t.name}</p>
                  {t.certified && <Star className="w-3.5 h-3.5 text-amber-500 shrink-0" title="Track complete" />}
                </div>
                <p className="text-[10px] text-[#9CA3AF] mb-1.5">{t.audience}</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div className="h-full bg-[#BA1826]" style={{ width: `${t.percent || 0}%` }} />
                  </div>
                  <span className="text-[10px] font-mono text-[#4B5563]">
                    {t.completed}/{t.total}
                  </span>
                </div>
                {t.remaining?.length > 0 && (
                  <button onClick={() => setActiveId(t.remaining[0])}
                    className="mt-1.5 text-[10px] text-[#BA1826] hover:underline"
                    data-testid={`cert-track-resume-${t.id}`}>
                    Resume → {(docs.find((d) => d.id === t.remaining[0]) || {}).title || t.remaining[0]}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <DocEditorDrawer open={showEditor && !!active} docId={active?.id}
        onClose={() => setShowEditor(false)} onSaved={load} />

      <div className="px-4 md:px-6 pb-6">
        <MyCertificates />
      </div>
    </div>
    </TranslatedPage>
  );
};

const DIFFICULTY_STYLES = {
  Beginner: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  Intermediate: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  Advanced: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500' },
};

const DifficultyBadge = ({ level, testid }) => {
  const style = DIFFICULTY_STYLES[level] || DIFFICULTY_STYLES.Intermediate;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${style.bg} ${style.text} ${style.border}`}
      data-testid={testid}>
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {level || 'Intermediate'}
    </span>
  );
};

const MediaEmbed = ({ embed, idx }) => {
  const url = embed.url || '';
  let src = '';
  if (embed.type === 'youtube') {
    const ytMatch = url.match(/(?:v=|youtu\.be\/|\/embed\/)([\w-]{11})/);
    if (ytMatch) src = `https://www.youtube.com/embed/${ytMatch[1]}`;
  } else if (embed.type === 'loom') {
    const loomMatch = url.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
    if (loomMatch) src = `https://www.loom.com/embed/${loomMatch[1]}`;
  } else if (embed.type === 'vimeo') {
    const v = url.match(/vimeo\.com\/(\d+)/);
    if (v) src = `https://player.vimeo.com/video/${v[1]}`;
  }
  if (embed.type === 'image') {
    return (
      <figure className="rounded-lg overflow-hidden border border-[#E5E7EB]"
        data-testid={`topic-embed-${idx}`}>
        <img src={url} alt={embed.title || 'Embedded image'} className="w-full" loading="lazy" />
        {(embed.title || embed.description) && (
          <figcaption className="px-3 py-2 text-xs text-[#4B5563] bg-[#FAFBFC]">
            {embed.title && <span className="font-semibold text-[#0A0A0A]">{embed.title}</span>}
            {embed.description && <span> — {embed.description}</span>}
          </figcaption>
        )}
      </figure>
    );
  }
  if (!src) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="text-sm text-[#BA1826] hover:underline inline-flex items-center gap-1"
        data-testid={`topic-embed-${idx}`}>
        <Video className="w-3.5 h-3.5" /> {embed.title || url}
      </a>
    );
  }
  return (
    <div className="rounded-lg overflow-hidden border border-[#E5E7EB]" data-testid={`topic-embed-${idx}`}>
      <div className="relative" style={{ paddingBottom: '56.25%' }}>
        <iframe src={src} title={embed.title || `Embed ${idx + 1}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full" />
      </div>
      {(embed.title || embed.description) && (
        <div className="px-3 py-2 text-xs text-[#4B5563] bg-[#FAFBFC]">
          {embed.title && <span className="font-semibold text-[#0A0A0A]">{embed.title}</span>}
          {embed.description && <span> — {embed.description}</span>}
        </div>
      )}
    </div>
  );
};

const COLOR = {
  brand: 'bg-[#FFF8F8] border-[#BA1826]/30 text-[#BA1826]',
  green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
};

const Section = ({ icon: Icon, title, body, accent, testid }) => (
  <div className={`mb-3 rounded-lg p-3 border ${COLOR[accent] || COLOR.brand}`} data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider font-bold mb-1 inline-flex items-center gap-1.5 opacity-80">
      <Icon className="w-3.5 h-3.5" /> {title}
    </p>
    <p className="text-sm text-[#0A0A0A] leading-relaxed whitespace-pre-line">{body || '—'}</p>
  </div>
);

const CertNav = ({ active, path, docs, setActiveId }) => {
  const idx = path.indexOf(active);
  const prev = idx > 0 ? path[idx - 1] : null;
  const next = idx < path.length - 1 ? path[idx + 1] : null;
  const docFor = (id) => docs.find((d) => d.id === id);
  return (
    <div className="mt-6 pt-4 border-t border-[#E5E7EB] flex items-center justify-between gap-3"
      data-testid="topic-cert-nav">
      {prev ? (
        <button onClick={() => setActiveId(prev)}
          className="text-xs text-left flex-1 px-3 py-2 border border-[#E5E7EB] rounded hover:bg-[#FAFBFC]">
          <p className="text-[10px] text-[#9CA3AF] mb-0.5">← Previous</p>
          <p className="font-semibold text-[#0A0A0A]">{docFor(prev)?.title || prev}</p>
        </button>
      ) : <div className="flex-1" />}
      {next ? (
        <button onClick={() => setActiveId(next)}
          className="text-xs text-right flex-1 px-3 py-2 border border-[#BA1826]/40 bg-[#FFF8F8] text-[#BA1826] rounded hover:bg-[#BA1826] hover:text-white transition-colors">
          <p className="text-[10px] opacity-80 mb-0.5">Next →</p>
          <p className="font-semibold">{docFor(next)?.title || next}</p>
        </button>
      ) : (
        <div className="flex-1 text-right text-xs text-emerald-700 font-semibold inline-flex items-center justify-end gap-1.5">
          <Star className="w-3.5 h-3.5 text-amber-500" /> You've reached the end of the certification path.
        </div>
      )}
    </div>
  );
};

export default AdminHelp;
